import { useState, useRef, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlayIcon,
  PauseIcon,
  BackwardIcon,
  ForwardIcon,
  SpeakerWaveIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';
import { API_URL } from '@/lib/api';
import { getDisplayTitleFromPdfPath } from '@/types/api';
import clsx from 'clsx';

interface QAAssets {
  success: boolean;
  job_id: string;
  exists: boolean;
  mp3?: {
    key: string;
    size: number;
    uploaded: string;
    download_url: string;
  };
  zip?: {
    key: string;
    size: number;
    uploaded: string;
    download_url: string;
  };
  metadata?: {
    key: string;
    size: number;
    uploaded: string;
    download_url: string;
  };
}

interface JobData {
  job_id: string;
  status: string;
  voice_id?: string;
  pdf_path?: string;
  tenant_id?: string;
  created_at?: number | string;
  finished_at?: number | string;
  metadata?: string;
}

interface ParsedMetadata {
  voice_id?: string;
  pdf_path?: string;
  isbn?: string;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function parseMetadata(metadataStr: string | undefined): ParsedMetadata {
  if (!metadataStr) return {};
  try {
    return JSON.parse(metadataStr);
  } catch {
    return {};
  }
}

export default function TTSPlayerPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qaAssets, setQaAssets] = useState<QAAssets | null>(null);
  const [job, setJob] = useState<JobData | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [audioLoaded, setAudioLoaded] = useState(false);

  // Fetch job and QA data
  useEffect(() => {
    if (!jobId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [jobResponse, qaResponse] = await Promise.all([
          fetch(`${API_URL}/tts/jobs/${jobId}`),
          fetch(`${API_URL}/tts/qa/${jobId}`),
        ]);

        if (jobResponse.ok) {
          const jobData = await jobResponse.json() as JobData;
          setJob(jobData);
        }

        if (qaResponse.ok) {
          const qaData = await qaResponse.json() as QAAssets;
          setQaAssets(qaData);
          
          if (!qaData.exists || !qaData.mp3) {
            setError('Audio file not available. Job may still be processing.');
          }
        } else {
          setError('Failed to load audio assets');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load player data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [jobId]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    const handleCanPlay = () => {
      console.log('Audio can play, duration:', audio.duration);
      setAudioLoaded(true);
    };
    const handleLoadedData = () => {
      console.log('Audio loaded data');
      setAudioLoaded(true);
    };
    const handleError = (e: Event) => {
      const audioEl = e.target as HTMLAudioElement;
      console.error('Audio error:', audioEl.error?.message, audioEl.error?.code);
      setError(`Failed to load audio: ${audioEl.error?.message || 'Unknown error'}`);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('error', handleError);

    // Try to load immediately
    audio.load();

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('error', handleError);
    };
  }, [qaAssets]);

  // Sync volume with audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  // Sync playback speed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch (err) {
        console.error('Play error:', err);
      }
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !progressRef.current || !duration) return;

    const rect = progressRef.current.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    audio.currentTime = clickPosition * duration;
  };

  const skip = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(duration, audio.currentTime + seconds));
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Get job info
  const metadata = parseMetadata(job?.metadata);
  const title =
    getDisplayTitleFromPdfPath(metadata.pdf_path) ||
    getDisplayTitleFromPdfPath(job?.pdf_path) ||
    'Unknown';
  const voice = metadata.voice_id || job?.voice_id || 'Unknown';

  // Build audio and download URLs (direct from TTS worker)
  const TTS_WORKER_URL = 'https://audiobook-staging-worker.andrea-4a3.workers.dev';
  
  const audioUrl = qaAssets?.mp3?.download_url 
    ? `${TTS_WORKER_URL}${qaAssets.mp3.download_url}`
    : null;

  const mp3DownloadUrl = audioUrl;
  const zipDownloadUrl = qaAssets?.zip?.download_url
    ? `${TTS_WORKER_URL}${qaAssets.zip.download_url}`
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Hidden Audio Element */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="auto"
        />
      )}

      {/* Back Button */}
      <Link
        to={`/tts/jobs/${jobId}`}
        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Job
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Audio Player</h1>
          <p className="text-slate-500 mt-1">Job: {jobId}</p>
        </div>
        <div className="flex gap-3">
          {mp3DownloadUrl && (
            <a href={mp3DownloadUrl} download>
              <Button variant="outline" leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}>
                Download MP3 ({qaAssets?.mp3 ? formatFileSize(qaAssets.mp3.size) : ''})
              </Button>
            </a>
          )}
          {zipDownloadUrl && (
            <a href={zipDownloadUrl} download>
              <Button variant="outline" leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}>
                Download ZIP ({qaAssets?.zip ? formatFileSize(qaAssets.zip.size) : ''})
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Card className="border-danger-200 bg-danger-50">
          <CardBody>
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-danger-500" />
              <div>
                <p className="font-semibold text-danger-700">Audio Unavailable</p>
                <p className="text-sm text-danger-600">{error}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Player Card */}
      {qaAssets?.exists && qaAssets.mp3 && (
        <Card>
          <CardBody>
            {/* Title & Info */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-slate-800 capitalize">📖 {title}</h2>
              <p className="text-slate-500 mt-1">
                Voice: {voice} | Duration: {formatTime(duration)} | Size: {formatFileSize(qaAssets.mp3.size)}
              </p>
            </div>

            {/* Loading State */}
            {!audioLoaded && (
              <div className="flex items-center justify-center gap-2 mb-4 text-slate-500">
                <Spinner size="sm" />
                <span>Loading audio...</span>
              </div>
            )}

            {/* Player Controls */}
            <div className="max-w-2xl mx-auto">
              {/* Progress Bar */}
              <div
                ref={progressRef}
                onClick={handleProgressClick}
                className="relative h-2 bg-slate-200 rounded-full cursor-pointer mb-2"
              >
                <div
                  className="absolute left-0 top-0 h-full bg-primary-400 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-primary-400 rounded-full shadow-md transition-all"
                  style={{ left: `calc(${progress}% - 8px)` }}
                />
              </div>

              {/* Time Display */}
              <div className="flex justify-between text-sm text-slate-500 mb-6">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>

              {/* Control Buttons */}
              <div className="flex items-center justify-center gap-4 mb-6">
                <button
                  className="p-3 rounded-full text-slate-600 hover:bg-slate-100 transition-colors"
                  onClick={() => skip(-10)}
                  title="Back 10 seconds"
                >
                  <BackwardIcon className="w-6 h-6" />
                </button>
                <button
                  onClick={togglePlay}
                  disabled={!audioLoaded}
                  className={clsx(
                    'w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-colors',
                    audioLoaded
                      ? 'bg-primary-400 text-white hover:bg-primary-500'
                      : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  )}
                >
                  {isPlaying ? (
                    <PauseIcon className="w-8 h-8" />
                  ) : (
                    <PlayIcon className="w-8 h-8 ml-1" />
                  )}
                </button>
                <button
                  className="p-3 rounded-full text-slate-600 hover:bg-slate-100 transition-colors"
                  onClick={() => skip(10)}
                  title="Forward 10 seconds"
                >
                  <ForwardIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Volume & Speed */}
              <div className="flex items-center justify-center gap-8">
                <div className="flex items-center gap-2">
                  <SpeakerWaveIcon className="w-5 h-5 text-slate-500" />
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={volume}
                    onChange={(e) => setVolume(parseInt(e.target.value))}
                    className="w-24 accent-primary-400"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Speed:</span>
                  {[0.5, 1, 1.5, 2].map((speed) => (
                    <button
                      key={speed}
                      onClick={() => setPlaybackSpeed(speed)}
                      className={clsx(
                        'px-2 py-1 rounded text-sm font-medium transition-colors',
                        playbackSpeed === speed
                          ? 'bg-primary-100 text-primary-600'
                          : 'text-slate-600 hover:bg-slate-100'
                      )}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Job Info */}
      <Card>
        <CardHeader title="Audio Details" />
        <CardBody>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-500">Job ID</p>
              <p className="font-mono text-sm text-slate-700">{jobId}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Voice</p>
              <p className="font-medium text-slate-700">{voice}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Status</p>
              <p className="font-medium text-slate-700 capitalize">{job?.status || 'Unknown'}</p>
            </div>
            {metadata.isbn && (
              <div>
                <p className="text-sm text-slate-500">ISBN</p>
                <p className="font-mono text-sm text-slate-700">{metadata.isbn}</p>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
